import {defineStore} from 'pinia'
import RequestMixin from "controllers/vue_components/mixins/request"

const INIT_DATA_TTL = 5 * 60 * 1000

export const useSettingsStore = defineStore('settings_page', {
    state: () => {
        return {
            current_user: {},
            countries_list: [],
            languages_list: [],
            qrcode_size_list: [],
            time_zone_list: [],
            time_formats_list: [],
            calendar_settings: {},
            date_formats_json: [],
            week_days_list: [],
            religions_list: [],
            json_images: [],
            profile_preview: false,
            profile_banner_settings: {},
            event_embed_url: null,
            policies: [],
            init_data_loading: false,
            init_data_loaded: false,
            init_data_fetched_at: null,
            error: null,
        }
    },

    getters: {
        isInitDataCacheValid: (state) =>
            state.init_data_fetched_at !== null &&
            Date.now() - state.init_data_fetched_at < INIT_DATA_TTL,
    },

    actions: {
        async fetchIfStale() {
            if (this.isInitDataCacheValid && this.init_data_loaded) return
            await this.fetchInitData()
        },

        async fetchInitData() {
            this.init_data_loading = true
            this.error = null
            try {
                const response = await RequestMixin.methods.axiosRequest('GET', '/api/v1/host/settings/init_data', {}, false)
                if (response.js_error === false && response.response.success) {
                    const data = response.response.data
                    this.$patch({
                        current_user: data.current_user,
                        countries_list: data.countries_list,
                        languages_list: data.languages_list,
                        time_zone_list: data.time_zone_list,
                        time_formats_list: data.time_formats_list,
                        qrcode_size_list: data.qrcode_size_list,
                        calendar_settings: data.calendar_settings,
                        date_formats_json: data.date_formats,
                        week_days_list: data.week_days_list,
                        religions_list: data.religions_list,
                        json_images: data.json_images,
                        init_data_loaded: true,
                        init_data_fetched_at: Date.now(),
                    })
                } else {
                    this.error = 'Failed to load settings.'
                }
            } catch {
                this.error = 'Something went wrong.'
            } finally {
                this.init_data_loading = false
            }
        },

        invalidate() {
            this.init_data_fetched_at = null
            this.init_data_loaded = false
        },

        async fetchProfileBannerSettings(force = false) {
            if (!force && Object.keys(this.profile_banner_settings).length > 0) return
            let response = await RequestMixin.methods.axiosRequest('GET', '/api/v1/host/public_profile_custom_templates/profile_banner_settings', {}, false)
            if(response.js_error == false && response.response.success) {
                this.profile_banner_settings = response.response.data
            } else {
                this.profile_banner_settings = {}
            }
        },

        async fetchCurrentUser() {
            let response = await RequestMixin.methods.axiosRequest('GET', '/api/v1/user/settings', {}, false)
            if(response.js_error == false && response.response.success) {
                this.current_user = response.response.data
            } else {
                this.current_user = {}
            }
        },

        async fetchUserPolicies() {
            if (this.policies.length > 0) return
            let response = await RequestMixin.methods.axiosRequest('GET', '/api/v1/host/settings/policies', {}, false)
            if(response.js_error == false && response.response.success) {
                this.policies = response.response.data
            } else {
                this.policies = []
            }
        },

        async updateCurrentUser(data) {
            let response = await RequestMixin.methods.axiosRequest('PUT', `/api/v1/user`, data, false)
            if (response.js_error === false && response.response?.success) {
                await this.fetchCurrentUser()
            }
            return response
        },

        async updateCurrentUserSocialLinks(data) {
            let response = await RequestMixin.methods.axiosRequest('PUT', `/api/v1/host/social_libraries`, data, false)
            if (response.js_error === false && response.response.success) {
                Object.assign(this.current_user.social_library, data.social_library)
            }
            return response
        }
    },
})
